import config from '../config/environment';
import supabase from '../config/supabase';
import type { NotesImportRow, NotesMatchResult } from '../utils/studentNotesImport';
import { handleServiceError } from '../utils/errorHandler';

const IMPORT_FUNCTION_NAME = 'import-student-notes';
const COMMIT_BATCH_SIZE = 200;
const ALREADY_IMPORTED_MESSAGE = 'Essas observações já estão cadastradas.';

export interface NotesImportCommitResult {
  importedCount: number;
}

interface CommitResponse {
  importedCount?: number;
  error?: string;
}

interface CommitRow {
  studentId: number;
  note: string;
  periodYear: number | null;
  periodMonth: number | null;
}

class StudentNotesImportServiceClass {
  private async getFunctionHeaders(): Promise<Record<string, string>> {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData?.session?.access_token;

    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      apikey: config.supabaseAnonKey,
    };
  }

  private async post<TResponse>(body: unknown, context: string): Promise<TResponse> {
    const headers = await this.getFunctionHeaders();
    const response = await fetch(`${config.supabaseFunctionsUrl}/${IMPORT_FUNCTION_NAME}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({})) as TResponse & { error?: string };

    if (!response.ok) {
      handleServiceError(new Error(payload?.error ?? `Erro ao ${context}`), context);
    }

    return payload;
  }

  async match(rows: readonly NotesImportRow[]): Promise<NotesMatchResult> {
    const studentNames = Array.from(new Set(rows.map((row) => row.studentName)));
    if (studentNames.length === 0) {
      return { studentsByName: {}, existingNotes: [] };
    }

    return this.post<NotesMatchResult>({
      action: 'match',
      studentNames,
    }, 'conferir alunas das observações');
  }

  async commit(rows: readonly NotesImportRow[]): Promise<NotesImportCommitResult> {
    const pending = rows.flatMap((row): CommitRow[] => {
      if (row.studentId === null) {
        return [];
      }
      return [{
        studentId: row.studentId,
        note: row.note,
        periodYear: row.periodYear,
        periodMonth: row.periodMonth,
      }];
    });

    let importedCount = 0;
    for (let offset = 0; offset < pending.length; offset += COMMIT_BATCH_SIZE) {
      const batch = pending.slice(offset, offset + COMMIT_BATCH_SIZE);
      try {
        const result = await this.post<CommitResponse>({
          action: 'commit',
          rows: batch,
        }, 'importar observações');
        importedCount += result.importedCount ?? 0;
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message === ALREADY_IMPORTED_MESSAGE) {
          continue;
        }
        if (importedCount > 0) {
          throw new Error(`A importação parou depois de ${importedCount} observações. ${message}`);
        }
        throw error;
      }
    }

    if (importedCount === 0) {
      throw new Error(ALREADY_IMPORTED_MESSAGE);
    }

    return { importedCount };
  }
}

export const StudentNotesImportService = new StudentNotesImportServiceClass();
